import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, Eye, EyeOff, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/auth";

/** Blog CRUD editor. */
export default function BlogEditor() {
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/blog`);
      setPosts(r.data || []);
      if (!selected && r.data?.length) setSelected(r.data[0].slug);
    } catch { toast.error("Could not load posts"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const current = posts.find((p) => p.slug === selected);

  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="admin-blog-panel">
      <div className="lg:col-span-4 space-y-3">
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
          <div className="flex items-center justify-between">
            <div className="overline">Blog posts</div>
            <button onClick={() => setCreating(true)} data-testid="blog-new" className="btn-pill btn-outline !text-xs !py-1.5">
              <Plus size={12} /> New
            </button>
          </div>
          <div className="mt-4 space-y-1.5">
            {posts.map((p) => (
              <button
                key={p.slug}
                onClick={() => { setSelected(p.slug); setCreating(false); }}
                data-testid={`blog-item-${p.slug}`}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                  selected === p.slug && !creating
                    ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                    : "hover:bg-blush/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{p.title}</span>
                  {p.published ? <Eye size={12} className="opacity-60 shrink-0" /> : <EyeOff size={12} className="opacity-60 shrink-0" />}
                </div>
                <div className="text-[10px] mt-0.5 opacity-60 tracking-widest uppercase truncate">
                  {p.slug} · {p.published ? "Live" : "Draft"}
                </div>
              </button>
            ))}
            {posts.length === 0 && <div className="text-xs text-[hsl(var(--onyx)/0.45)] p-2 italic">No posts yet.</div>}
          </div>
        </div>
      </div>
      <div className="lg:col-span-8">
        {creating ? (
          <BlogCreate onCreated={(slug) => { setCreating(false); setSelected(slug); load(); }} onCancel={() => setCreating(false)} />
        ) : current ? (
          <BlogForm key={current.slug} post={current} onSaved={load} />
        ) : (
          <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
            Pick or create a post.
          </div>
        )}
      </div>
    </div>
  );
}

function BlogCreate({ onCreated, onCancel }) {
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", body_md: "# Hello\n\nStart writing…", author: "Malek Dridi", tags: [], published: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9-]+$/.test(form.slug)) return toast.error("Slug must be lowercase letters/numbers/hyphens");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/blog`, form);
      toast.success("Post created");
      onCreated(form.slug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create post"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="blog-create-form">
      <div className="flex items-center justify-between">
        <div className="overline">New post</div>
        <button type="button" onClick={onCancel} className="text-xs text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))]">Cancel</button>
      </div>
      <Input label="Slug" required value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase() })} testid="new-blog-slug" placeholder="post-url-slug" />
      <Input label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="new-blog-title" />
      <TextArea label="Excerpt (card blurb)" rows={2} value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} />
      <TextArea label="Body (Markdown)" rows={8} value={form.body_md} onChange={(v) => setForm({ ...form, body_md: v })} />
      <button type="submit" disabled={busy} data-testid="blog-create-submit" className="btn-pill btn-primary disabled:opacity-60">
        {busy ? "Creating…" : "Create post"}
      </button>
    </form>
  );
}

function BlogForm({ post, onSaved }) {
  const [form, setForm] = useState(post);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  useEffect(() => { setForm(post); }, [post]);
  const dirty = JSON.stringify(form) !== JSON.stringify(post);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/blog/${post.slug}`, {
        title: form.title, excerpt: form.excerpt, cover_image: form.cover_image,
        body_md: form.body_md, author: form.author, tags: form.tags || [], published: !!form.published,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadCover = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/blog/${post.slug}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: r.data.image }));
      toast.success("Cover uploaded");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/blog/${post.slug}`); toast.success("Deleted"); onSaved(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid={`blog-edit-${post.slug}`}>
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Editing</div>
          <h3 className="font-display text-2xl mt-1 tracking-tight">{post.title}</h3>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">/blog/{post.slug}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="btn-pill btn-outline !text-xs"><ExternalLink size={12} /> Preview live</a>
          <button onClick={save} disabled={saving || !dirty} data-testid="blog-save" className="btn-pill btn-primary disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
          <button onClick={remove} className="btn-pill btn-outline" data-testid="blog-delete"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Basics</div>
        <Input label="Title" value={form.title || ""} onChange={(v) => setForm({ ...form, title: v })} />
        <TextArea label="Excerpt" rows={2} value={form.excerpt || ""} onChange={(v) => setForm({ ...form, excerpt: v })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Author" value={form.author || ""} onChange={(v) => setForm({ ...form, author: v })} />
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-published" />
            Published (visible on site)
          </label>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Tags</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(form.tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blush/50 text-sm">
                {t}
                <button type="button" onClick={() => setForm({ ...form, tags: form.tags.filter((_, j) => j !== i) })} className="opacity-60 hover:opacity-100">✕</button>
              </span>
            ))}
            <input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagDraft.trim()) {
                  e.preventDefault();
                  setForm({ ...form, tags: [...(form.tags || []), tagDraft.trim()] });
                  setTagDraft("");
                }
              }}
              placeholder="Add tag & press Enter"
              className="h-9 rounded-full border border-[hsl(var(--onyx)/0.15)] px-4 text-xs outline-none focus:border-[hsl(var(--onyx))]"
            />
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3">
        <div className="overline">Cover image</div>
        {form.cover_image && (
          <img src={form.cover_image} alt="" className="w-full max-w-md rounded-2xl" />
        )}
        <label htmlFor="blog-cover-file" className={`block w-full text-center py-4 rounded-2xl border-2 border-dashed cursor-pointer ${uploading ? "border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.35)]" : "border-[hsl(var(--onyx)/0.2)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))]"}`}>
          {uploading ? "Uploading…" : <><Upload size={16} className="inline mr-2" /> Upload cover</>}
        </label>
        <input id="blog-cover-file" type="file" accept="image/*" disabled={uploading}
          onChange={(e) => { uploadCover(e.target.files); e.target.value = ""; }} className="hidden" />
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">Body (Markdown)</div>
          <button type="button" onClick={() => setShowPreview((p) => !p)} className="text-xs text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))]">
            {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
        {showPreview ? (
          <div className="prose max-w-none p-4 rounded-2xl bg-blush/20 text-[hsl(var(--onyx)/0.85)]" data-testid="blog-preview">
            <ReactMarkdown>{form.body_md || ""}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={form.body_md || ""}
            rows={22}
            onChange={(e) => setForm({ ...form, body_md: e.target.value })}
            data-testid="blog-body"
            className="w-full rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-3 text-sm font-mono outline-none focus:border-[hsl(var(--onyx))] resize-y"
            placeholder="# Heading&#10;&#10;Write your post in **Markdown**..."
          />
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, placeholder, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testid}
        className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <textarea value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y" />
    </div>
  );
}
